import { Fragment, useState, type ReactNode } from "react";
import s from "./Reliability.module.css";
import { Page } from "../../layout/AppShell";
import { useFits, usePopulations } from "../../api/queries";
import { censoredPct, shape, SHAPE_LABEL, type Fit, type Population } from "../../domain/reliability";
import { Card, EmptyState, ErrorState, Skeleton, Table } from "../../design/components";
import { formatDateTime } from "../../lib/format";
import { locale, t } from "../../i18n";

/**
 * Reliability, with the units that have not failed kept in.
 *
 * The classic error is fitting on failure times alone and dropping every unit
 * still running. It does not cancel out: dropping the survivors removes
 * exactly the long-lived units, so the fleet looks like it fails sooner than
 * it does. Under-estimating life is not the safe way to be wrong — it replaces
 * serviceable fittings, floods the operator with alerts that never come true,
 * and ends with somebody switching the predictions off.
 *
 * So the censored count is the headline on every fit, and the shape is read
 * from the CONFIDENCE INTERVAL rather than the point estimate: a beta of 1.2
 * whose interval spans 0.8 to 1.9 says nothing about wear-out, and calling it
 * wear-out would licence an age-based replacement programme on a population
 * that may fail at a constant rate.
 */
export function ReliabilityPage() {
  const populations = usePopulations();
  const [selected, setSelected] = useState<string | null>(null);
  const fits = useFits(selected);

  return (
    <Page eyebrow="Deep thinking" title="How the fleet actually fails">
      {populations.isPending && <Skeleton rows={5} />}
      {populations.isError && <ErrorState error={populations.error} retry={() => populations.refetch()} />}
      {populations.data && (
        <Card
          title="Populations"
          sub="Units and failures. The gap between them is what a failures-only fit would throw away."
        >
          <Table>
            <thead>
              <tr>
                <th>{t("Population")}</th>
                <th>{t("Measured in")}</th>
                <th className={s.num}>{t("Units")}</th>
                <th className={s.num}>{t("Failures")}</th>
                <th className={s.num}>{t("Still running")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {/* Keyed on population AND time unit. The same asset family can
                  appear twice measured in different bases — the platform
                  returns `mixed` in both cycles and hours — and those are not
                  the same population: twelve hours and one cycle cannot be
                  pooled into one fit. The unit is shown for the same reason,
                  so the two rows are distinguishable to a reader and not only
                  to React. */}
              {populations.data.map((p) => (
                <PopulationRow key={`${p.population}|${p.timeUnit}`} p={p} onSelect={() => setSelected(p.population)} />
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {selected && fits.isPending && <Skeleton rows={4} />}
      {selected && fits.isError && <ErrorState error={fits.error} retry={() => fits.refetch()} />}
      {selected && fits.data && fits.data.length === 0 && (
        <EmptyState>{t("No fit has been recorded for {population}.", { population: selected })}</EmptyState>
      )}
      {fits.data && fits.data.map((f) => <FitCard key={f.id} fit={f} />)}
    </Page>
  );
}

/** A translated sentence with markup: each `{name}` is replaced by its node. */
function rich(text: string, nodes: Record<string, ReactNode>): ReactNode {
  return text
    .split(/\{(\w+)\}/)
    .map((part, i) => <Fragment key={i}>{i % 2 ? (nodes[part] ?? `{${part}}`) : part}</Fragment>);
}

function PopulationRow({ p, onSelect }: { p: Population; onSelect: () => void }) {
  const running = p.units - p.failures;
  return (
    <tr>
      <td className={s.name}>{p.population}</td>
      <td>{p.timeUnit}</td>
      <td className={s.num}>{p.units}</td>
      <td className={s.num}>{p.failures}</td>
      <td className={s.num}>{running}</td>
      <td>
        {p.fittable ? (
          <button type="button" onClick={onSelect}>
            {t("Show the fit")}
          </button>
        ) : (
          /* not "no fit" — a statement about the data, not about the life */
          <span className={s.verdict}>{p.whyNot ?? t("cannot be fitted")}</span>
        )}
      </td>
    </tr>
  );
}

function FitCard({ fit }: { fit: Fit }) {
  const sh = shape(fit);
  const pct = censoredPct(fit);
  return (
    <div className={s.fit} data-testid={`fit-${fit.population}`}>
      <div className={s.head}>
        <span className={s.name}>{fit.population}</span>
        <span className={s.chip}>{t(SHAPE_LABEL[sh])}</span>
      </div>

      {/* The headline: what a failures-only fit would have deleted. */}
      <div className={s.kept}>
        {rich(
          pct != null
            ? t(
                "{censored} of {units} units have not failed and are kept in the fit — {pct}% of the sample. Dropping them is the classic error, and it biases the estimated life downwards, never up.",
                { units: fit.units, pct },
              )
            : t(
                "{censored} of {units} units have not failed and are kept in the fit. Dropping them is the classic error, and it biases the estimated life downwards, never up.",
                { units: fit.units },
              ),
          { censored: <span className={s.keptBig}>{fit.censored}</span> },
        )}
      </div>

      <div className={s.stats}>
        <div className={s.stat}>
          <span>{t("Shape (beta)")}</span>
          <b>
            {fit.beta.toFixed(2)}
            {fit.betaLow != null && fit.betaHigh != null
              ? ` (${fit.betaLow.toFixed(2)}–${fit.betaHigh.toFixed(2)})`
              : ""}
          </b>
        </div>
        <div className={s.stat}>
          <span>{t("Characteristic life")}</span>
          <b>
            {Math.round(fit.eta).toLocaleString(locale())} {fit.timeUnit}
          </b>
        </div>
        <div className={s.stat}>
          <span>{t("Failures")}</span>
          <b>{fit.failures}</b>
        </div>
      </div>

      {fit.shapeVerdict && <div className={s.verdict}>{fit.shapeVerdict}</div>}
      {fit.wearOut?.verdict && <div className={s.verdict}>{fit.wearOut.verdict}</div>}
      {fit.wearOut?.note && <div className={s.verdict}>{fit.wearOut.note}</div>}

      {/* Every figure past the last observed failure is a model output. */}
      {fit.lastFailureAt != null && (
        <div className={s.extrapolated}>
          {fit.eta > fit.lastFailureAt
            ? t(
                "The latest observed failure was at {at} {unit}. The characteristic life above is beyond that point — a property of the model rather than something the fleet has demonstrated.",
                { at: Math.round(fit.lastFailureAt).toLocaleString(locale()), unit: fit.timeUnit },
              )
            : t(
                "The latest observed failure was at {at} {unit}. The characteristic life above is within it — inside the range the fleet has actually demonstrated.",
                { at: Math.round(fit.lastFailureAt).toLocaleString(locale()), unit: fit.timeUnit },
              )}
        </div>
      )}

      {fit.fittedAt && (
        <div className={s.chip}>
          {t("Fitted {when} · {method}", { when: formatDateTime(fit.fittedAt), method: fit.method })}
        </div>
      )}
    </div>
  );
}
